import { AxiosInstance, AxiosRequestConfig } from 'axios'

import { SESSION_ID_HEADER } from '@/constant/app'
import { shouldUseJwtAuth } from '@/utils/tenant.utils'
import {
  formatVideoUploadDuplicateMessage,
  throwIfVideoUploadDuplicate,
} from '@/utils/video-upload-duplicate-error'

import { baseService } from './base.service'
import { IBaseService } from './interfaces/base-service.interface'

import { QueryParams } from '@/types/base.type'
import { ListResponse } from '@/types/list.type'
import { GetResourceType, ResourceType } from '@/types/resource.type'

export class ResourceService {
  private static instance: ResourceService
  private readonly api: AxiosInstance

  private constructor(private readonly baseService: IBaseService) {
    this.api = this.baseService.getClient()
  }

  public static getInstance(baseService: IBaseService): ResourceService {
    if (!ResourceService.instance) {
      ResourceService.instance = new ResourceService(baseService)
    }
    return ResourceService.instance
  }

  getResource = async (
    {
      queryParams,
      getResource,
    }: {
      queryParams: QueryParams
      getResource: GetResourceType
    },
    options?: AxiosRequestConfig
  ) => {
    try {
      const { pageNumber, pageSize, sort, search } = queryParams
      let url = '/resource/'
      if (pageNumber && pageSize) {
        url += `?pageNumber=${pageNumber}&pageSize=${pageSize}`
      }
      if (sort) {
        url += `&sort=${sort}`
      }
      if (search) {
        url += `&search=${encodeURIComponent(search)}`
      }
      const { data } = await this.api.post<ListResponse<ResourceType>>(url, getResource, options)
      return data
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
    } catch (error: any) {
      throw new Error(error.response.data.message)
    }
  }

  getDetailResource = async (uuid: string, options?: AxiosRequestConfig) => {
    const { data } = await this.api.get<ResourceType>(`/resource/${uuid}`, options)
    return data
  }

  deleteTemporaryResource = async ({ uuid, key }: { uuid: string; key: string }, options?: AxiosRequestConfig) => {
    const { data } = await this.api.put(`/resource/${uuid}/temporary`, { key }, options)
    return data
  }

  deleteResource = async (id: number) => {
    try {
      const { data } = await this.api.delete(`/resource/delete/${id}`)
      return data
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
    } catch (error: any) {
      throw new Error(error.response.data.message)
    }
  }

  createResource = async ({
    files,
    folderPath,
    isProtected,
    isDownloadable = false,
    onProgress,
  }: {
    files: File[]
    folderPath: string
    isProtected: boolean
    isDownloadable?: boolean
    onProgress?: (progress: number) => void
  }) => {
    const formData = new FormData()
    files.forEach(file => {
      formData.append('files', file)
    })
    formData.append('folderPath', folderPath)
    formData.append('isProtected', isProtected ? 'true' : 'false')
    formData.append('isDownloadable', String(isDownloadable ?? false))

    try {
      // If we have a progress callback, use XMLHttpRequest instead of axios
      if (onProgress) {
        return new Promise<unknown>((resolve, reject) => {
          const xhr = new XMLHttpRequest()

          // Track upload progress with throttling to avoid too many updates
          let lastProgressUpdate = 0
          let lastProgressValue = 0

          xhr.upload.addEventListener('progress', event => {
            if (event.lengthComputable) {
              const now = Date.now()
              const percentComplete = Math.min(99, Math.floor((event.loaded / event.total) * 100))

              // Only update if progress has changed significantly or enough time has passed
              // This prevents UI jitter from too many small updates
              if (
                percentComplete > lastProgressValue ||
                percentComplete - lastProgressValue >= 5 ||
                now - lastProgressUpdate > 200
              ) {
                lastProgressUpdate = now
                lastProgressValue = percentComplete
                onProgress(percentComplete)
              }
            }
          })

          xhr.addEventListener('load', () => {
            if (xhr.status >= 200 && xhr.status < 300) {
              try {
                // Complete the progress to 100% when the server has processed the upload
                onProgress(100)

                const data = JSON.parse(xhr.responseText)
                resolve(data)
              } catch {
                onProgress(0)
                reject(new Error('Invalid response format'))
              }
            } else if (xhr.status === 409) {
              onProgress(0)
              try {
                const body = JSON.parse(xhr.responseText || '{}') as {
                  message?: string
                  videoId?: string
                  videoUrl?: string
                  fileName?: string | null
                  uploadType?: string | null
                }
                reject(
                  new Error(formatVideoUploadDuplicateMessage(body))
                )
              } catch {
                reject(new Error('This video has already been uploaded'))
              }
            } else {
              onProgress(0)
              let message = xhr.statusText || 'Upload failed'
              try {
                const body = JSON.parse(xhr.responseText || '{}') as {
                  message?: string
                }
                if (body.message) {
                  message = body.message
                }
              } catch {
                // keep default message
              }
              reject(new Error(message))
            }
          })

          xhr.addEventListener('error', () => {
            onProgress(0)
            reject(new Error('Network error during upload'))
          })

          xhr.addEventListener('abort', () => {
            onProgress(0)
            reject(new Error('Upload aborted'))
          })

          // Get the base URL from the axios instance
          const baseURL = this.api.defaults.baseURL || ''
          // Ensure proper URL construction with a single slash
          const url = baseURL.endsWith('/') ? `${baseURL}resource/create` : `${baseURL}/resource/create`
          xhr.open('POST', url)

          // IMPORTANT: Do NOT set Content-Type header for multipart/form-data
          // The browser will automatically set the correct Content-Type with boundary

          // JWT mode (non-fsds): Bearer token. FSDS mode: session cookie header.
          const isJwtMode = typeof window !== 'undefined' && shouldUseJwtAuth()
          if (isJwtMode) {
            const accessToken = this.baseService.getAccessToken()
            if (accessToken) {
              xhr.setRequestHeader('Authorization', `Bearer ${accessToken}`)
            }
            xhr.withCredentials = false
          } else {
            const sessionId = this.api.defaults.headers[SESSION_ID_HEADER] as string
            if (sessionId) {
              xhr.setRequestHeader(SESSION_ID_HEADER, sessionId)
            }
            xhr.withCredentials = true
          }

          // Copy non-auth headers from axios defaults
          const headers = this.api.defaults.headers.common || {}
          Object.keys(headers).forEach(key => {
            if (key !== 'Content-Type' && key !== 'Authorization' && key !== SESSION_ID_HEADER) {
              xhr.setRequestHeader(key, headers[key] as string)
            }
          })

          xhr.send(formData)
        })
      } else {
        // Use regular axios if no progress tracking needed
        const { data } = await this.api.post('/resource/create', formData, {
          headers: {
            'Content-Type': 'multipart/form-data',
          },
        })
        return data
      }
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
    } catch (error: unknown) {
      const axiosError = error as {
        response?: { status?: number; data?: { message?: string } }
      }
      if (axiosError.response?.status === 409) {
        throwIfVideoUploadDuplicate(error)
      }
      throw new Error(axiosError.response?.data?.message || 'File upload failed')
    }
  }

  createResourceFolder = async ({
    folderName,
    folderPath,
    isProtected,
    isDownloadable = false,
  }: {
    folderName: string
    folderPath: string
    isProtected: boolean
    isDownloadable?: boolean
  }) => {
    try {
      const { data } = await this.api.post('/resource/create-folder', {
        folderName,
        folderPath,
        isProtected,
        isDownloadable,
      })
      return data
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
    } catch (error: any) {
      throw new Error(error.response.data.message || 'File upload failed')
    }
  }

  updateResource = async (
    id: number,
    updateData: { fileName?: string; folderName?: string; isProtected?: boolean; isDownloadable?: boolean }
  ) => {
    try {
      const { data } = await this.api.put(`/resource/update/${id}`, updateData)
      return data
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
    } catch (error: any) {
      throw new Error(error.response.data.message)
    }
  }
}

const resourceService = ResourceService.getInstance(baseService)

export default resourceService
